import { MapPin, Users } from "lucide-react";
import { Link } from "wouter";
import { useCallback, useRef } from "react";
import type { Companion } from "@shared/schema";

function isDuoCompanion(companion: Companion): boolean {
  return companion.services?.some(s => s.toLowerCase().includes("bi duo") || s.toLowerCase() === "duo") ?? false;
}

function AvailabilityBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    "Available Now": "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    "Later Today": "bg-amber-500/10 text-amber-400 border-amber-500/20",
    "Book Ahead": "bg-sky-500/10 text-sky-400 border-sky-500/20",
    "Busy": "bg-red-500/10 text-red-400 border-red-500/20",
  };

  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-medium border uppercase tracking-wider ${
        colors[type] || colors["Book Ahead"]
      }`}
    >
      {type}
    </span>
  );
}

interface CompanionCardProps {
  companion: Companion;
}

export function CompanionCard({ companion }: CompanionCardProps) {
  const primaryImage = companion.images?.[0] || "https://images.unsplash.com/photo-1678723357379-d87f2a0ec8ec?auto=format&fit=crop&q=60&w=400";
  const hasDuo = isDuoCompanion(companion);
  const retryCount = useRef(0);

  // Retry loading image up to 2 times on error (handles SW interference, transient network issues)
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (retryCount.current < 2) {
      retryCount.current += 1;
      // Append cache-buster to force fresh fetch, bypassing any stale SW/browser cache
      const sep = img.src.includes("?") ? "&" : "?";
      img.src = primaryImage + sep + "_r=" + retryCount.current;
    }
  }, [primaryImage]);

  return (
    <Link href={`/companion/${companion.id}`}>
      <div
        data-testid={`card-companion-${companion.id}`}
        className="group cursor-pointer bg-[#1A1A1A] rounded-xl overflow-hidden border border-gray-800 hover:border-[#D4AF37]/50 transition-all duration-500 shadow-2xl block hover:-translate-y-2"
      >
        <div className="relative aspect-[3/4] overflow-hidden">
          <img
            src={primaryImage}
            alt={`Manchester escort ${companion.name} - verified companion available for incall and outcall bookings`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
            decoding="async"
            width={400}
            height={533}
            onError={handleImageError}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60" />
          <div className="absolute top-4 left-4 flex gap-2">
            <AvailabilityBadge type={companion.availability} />
            {hasDuo && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium border uppercase tracking-wider bg-purple-500/10 text-purple-400 border-purple-500/20 flex items-center gap-1">
                <Users size={9} />
                DUO
              </span>
            )}
          </div>
          <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
            <div>
              <h3 className="text-xl font-light text-white mb-1 tracking-wide">
                {companion.name}
              </h3>
              <div className="flex items-center gap-2 text-[9px] text-gray-400 uppercase tracking-widest font-semibold">
                <MapPin size={10} className="text-[#D4AF37]" />
                {companion.location.split(",")[0]}
              </div>
            </div>
            <div className="text-transparent bg-clip-text bg-gradient-to-r from-[#F1D279] to-[#D4AF37] font-bold text-sm tracking-tighter">
              {companion.price}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export { AvailabilityBadge };
